/**
 * Bank Account Table Component
 * 
 * Displays a table of bank accounts.
 */

import * as React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Edit, CreditCard, Trash2 } from "lucide-react";
import type { BankAccount } from "@/services/bankAccountService";

interface BankAccountTableProps {
  bankAccounts: BankAccount[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onEdit: (account: BankAccount) => void;
  onDelete?: (account: BankAccount) => void;
  /** Edit/delete (requires manage_payments on API). Default true. */
  allowMutate?: boolean;
}

export function BankAccountTable({ 
  bankAccounts, 
  loading, 
  error, 
  onRefresh,
  onEdit,
  onDelete,
  allowMutate = true,
}: BankAccountTableProps) {
  const getBankName = (bank: string | { _id: string; name: string; code: string }) => {
    if (typeof bank === "string") {
      return bank;
    }
    return bank.name;
  };

  const getBankCode = (bank: string | { _id: string; name: string; code: string }) => {
    if (typeof bank === "string") {
      return "";
    }
    return bank.code;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-32 gap-2">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Coba Lagi
        </Button>
      </div>
    );
  }

  if (bankAccounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 gap-2 text-muted-foreground">
        <CreditCard className="h-8 w-8" />
        <p className="text-sm">Belum ada rekening yang terdaftar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Total: {bankAccounts.length} rekening
        </p>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provinsi</TableHead>
              <TableHead>Kota/Kabupaten</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead>Nomor Rekening</TableHead>
              <TableHead>Nama Rekening</TableHead>
              <TableHead>Status</TableHead>
              {allowMutate && <TableHead className="text-right">Aksi</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {bankAccounts.map((account) => (
              <TableRow key={account._id}>
                <TableCell>{account.province || "-"}</TableCell>
                <TableCell>{account.city || "-"}</TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">{getBankName(account.bank)}</div>
                    {getBankCode(account.bank) && (
                      <div className="text-xs text-muted-foreground">
                        {getBankCode(account.bank)}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-mono">{account.accountNumber}</TableCell>
                <TableCell>{account.accountName}</TableCell>
                <TableCell>
                  <Badge variant={account.isActive ? "default" : "secondary"}>
                    {account.isActive ? "Aktif" : "Tidak Aktif"}
                  </Badge>
                </TableCell>
                {allowMutate && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(account)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {onDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(account)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

